import {
  useMutation,
  useMutationState,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";

import {
  getIxdMatchWorkbookTable,
  getIxdValueSyncPlan,
} from "@/apis/ixd-value-sync/route";
import type { IxdMatchPreviewResult } from "@/apis/ixd-value-sync/types";
import { updateAutoSumState } from "@/apis/preview/v2/route";
import queries from "@/queries";
import { replaceWorkbookTable } from "./replace-workbook-table";

export interface ToggleWorkbookAutoSumInput {
  tableId: string;
  roleKey: string;
  useAutoSum: boolean;
  /** 매칭된 IXD 표의 방향 — entry.ixd.dlView.isTransposed. 없으면 비전치 */
  transpose: boolean;
}

const MUTATION_KEY = ["toggle-workbook-auto-sum"];

/**
 * 매칭 뷰에서 워크북 표의 합계 자동계산을 바꾼다.
 *
 * 저장(PATCH auto-sum-state) 뒤 그 표만 다시 받아 preview 캐시의 해당 entry를 갈아
 * 끼운다 — preview 전체를 다시 받지 않는다. plan은 자동합계에 따라 값이 달라지므로
 * 저장 직후 새로 받아 캐시에 넣고, 도착할 때까지 mutation을 pending으로 둔다 —
 * "이 표의 plan이 아직 이전 값"이라는 사실을 useIsWorkbookPlanRefreshing이 표 단위로
 * 읽을 수 있게 하기 위해서다.
 *
 * plan 갱신은 invalidate+refetch가 아니라 GET을 직접 기다린다. refetch promise는
 * 다른 곳의 invalidateQueries(cancelRefetch)가 취소하면 조용히 일찍 resolve돼,
 * 캐시가 이전 값인 채로 스피너가 꺼진다. 연달아 토글하면 scope로 직렬로 돌아
 * 뒤 토글의 GET이 앞 토글의 저장 이후에 시작된다 — 각자 자기 GET만 기다리면 된다.
 */
export const useToggleWorkbookAutoSum = ({
  workbookId,
  reportId,
}: {
  workbookId: string;
  reportId: string;
}) => {
  const queryClient = useQueryClient();
  const previewKey = queries
    .workbook(workbookId)
    ._ctx.ixdMatchPreview(reportId).queryKey;
  const planKey = queries
    .workbook(workbookId)
    ._ctx.ixdValueSyncPlan(reportId).queryKey;

  return useMutation({
    mutationKey: MUTATION_KEY,
    mutationFn: async ({
      tableId,
      roleKey,
      useAutoSum,
      transpose,
    }: ToggleWorkbookAutoSumInput) => {
      await updateAutoSumState(workbookId, tableId, { roleKey, useAutoSum });
      // 표 GET이 실패해도 서버 상태는 이미 바뀌었으므로, plan은 표 GET과
      // 무관하게 받아서 도착하는 대로 캐시에 쓴다.
      const planRefresh = getIxdValueSyncPlan(workbookId, reportId).then(
        (plan) => {
          queryClient.setQueryData(planKey, plan);
        },
      );
      // 표 GET이 먼저 던지면 아래 await에 닿지 못한다 — unhandled rejection 방지.
      planRefresh.catch(() => undefined);
      const tables = await getIxdMatchWorkbookTable(workbookId, tableId, {
        roleKey,
        transpose,
      });
      queryClient.setQueryData<IxdMatchPreviewResult>(previewKey, (old) =>
        old ? replaceWorkbookTable(old, tableId, roleKey, tables) : old,
      );
      try {
        await planRefresh;
      } catch {
        // 저장은 이미 성공했다 — mutation을 실패로 돌리면 onError가 "설정 변경
        // 실패"라는 틀린 안내를 하게 된다. 실패를 그대로 알리고, 무효화를 걸어
        // 활성 화면의 자동 refetch에 복구를 맡긴다.
        void queryClient.invalidateQueries({ queryKey: planKey, exact: true });
        toast.error(
          "합계 자동계산은 저장됐지만 반영값 갱신에 실패했습니다. 값 반영 시 이전 값이 들어갈 수 있습니다.",
          { duration: 3000 },
        );
      }
    },
    // 같은 plan 캐시를 쓰는 토글이 겹치지 않게 직렬로 돈다. useMutationState는
    // 대기 중인 mutation도 pending으로 보므로 뒤 토글의 스피너는 즉시 돈다.
    scope: { id: "toggle-workbook-auto-sum" },
    onError: () => {
      toast.error("합계 자동계산 설정 변경에 실패했습니다.", {
        duration: 3000,
      });
    },
  });
};

/**
 * 이 워크북 표의 자동합계가 바뀌어 plan을 다시 받는 중인가.
 *
 * 워크북 표 하나에 role이 여럿일 수 있고 자동합계는 role 단위로 저장되므로
 * (tableId, roleKey)로 본다 — tableId만 보면 같은 표의 다른 role 행까지 돈다.
 */
export const useIsWorkbookPlanRefreshing = (
  workbookTableId: string | undefined,
  workbookRoleKey: string | undefined,
): boolean => {
  const pending = useMutationState({
    filters: { mutationKey: MUTATION_KEY, status: "pending" },
    select: (mutation) => {
      const variables = mutation.state.variables as
        | ToggleWorkbookAutoSumInput
        | undefined;
      return variables ? `${variables.tableId}\u0000${variables.roleKey}` : "";
    },
  });
  return (
    workbookTableId !== undefined &&
    workbookRoleKey !== undefined &&
    pending.includes(`${workbookTableId}\u0000${workbookRoleKey}`)
  );
};
